/**
 * The sidebar's "Chats" section: the current project's conversations, each
 * carrying its own state.
 *
 * The history was a rail on the chat's right-hand side, and "Running" (#193) a
 * separate, cross-project list in this sidebar. With the rail merged in, the
 * rule is one sentence (owner): **the section shows the threads of the selected
 * project, and a thread's status follows the thread.**
 *
 * - A conversation that is running or waiting on an approval is not pinned
 *   anywhere else — it says so **on its own row**: a spinner while it works, the
 *   approval pulse and "Needs approval" when it waits on the user. Leaving the
 *   chat for another tab does not stop the turn (#189); this row is the way back.
 * - Live rows lead the list, so an old conversation that was resumed does not
 *   hide below "View all" while it runs; the rest are the newest, one line each.
 * - **Another project's conversations are not here**, running or not. Switching
 *   project shows that project's threads, with their states, and nothing of the
 *   one just left. A turn left running in another project keeps running and is found by
 *   switching back.
 * - **"View all"** when the project holds more than fit. A project can have a
 *   hundred conversations; the sidebar is not the place to scroll them, so the
 *   full list is a page with search (`ChatsPane`).
 * - A running **skill edit** (#196) is not a thread of any project, so it keeps
 *   a row of its own at the head of the section and reopens its editor.
 *
 * A thread list left this sidebar once, because the list is per project and the
 * sidebar belonged to no project. It does now: the project row sits right above
 * this section and names whose conversations these are, and a project switch
 * shows loading rather than the previous project's rows (`useThreads`).
 */
import { useSyncExternalStore } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import type { ThreadsState } from "../hooks/useThreads";
import type { ChatSession, ChatSessions } from "../lib/chatSessions";
import type { SkillEditSessions } from "../lib/skillEditSessions";
import { humanizeMentionMarkers } from "../lib/mentionUtils";
import { recentThreads } from "../lib/sidebarChats";
import { Spinner } from "./settings/Spinner";

/** The conversation's first question, as plain text — what the user asked it to do. */
export function conversationTitle(session: ChatSession, fallback: string): string {
  const first = session.state.messages.find((m) => m.role === "user" && m.text.trim() !== "");
  const text = first ? humanizeMentionMarkers(first.text).replace(/\s+/g, " ").trim() : "";
  return text || fallback;
}

/** A conversation waits on the user: its turn has stopped on an approval. */
function isWaiting(session: ChatSession): boolean {
  return !session.state.busy && session.state.interrupt !== null;
}

/** The live state of a row: a spinner while working, the approval pulse when waiting. */
function LiveIcon({ waiting }: { waiting: boolean }) {
  return (
    <span className="app-nav-icon" aria-hidden="true">
      {waiting ? (
        <span className="approval-pulse">
          <i />
          <i />
        </span>
      ) : (
        <Spinner size={14} />
      )}
    </span>
  );
}

export function SidebarChats({
  sessions,
  skillEdits,
  currentProjectId,
  currentThreadId,
  threads,
  viewAllActive,
  onOpen,
  onOpenSkill,
  onViewAll,
  onRetry,
}: {
  sessions: ChatSessions;
  /** Skill edits run on too (#196): "Editing: <skill>", reopening that skill's editor. */
  skillEdits: SkillEditSessions;
  /** The project the list belongs to; null when none is selected. */
  currentProjectId: string | null;
  /** The conversation on screen, when the chat is the open pane. */
  currentThreadId: string | null;
  /** The current project's conversations, newest first. */
  threads: ThreadsState;
  /** The Chats page is the open pane. */
  viewAllActive: boolean;
  onOpen: (threadId: string, projectId: string | null) => void;
  onOpenSkill: (skillId: string) => void;
  onViewAll: () => void;
  onRetry: () => void;
}) {
  const { t } = useLocale();
  useSyncExternalStore(sessions.subscribe, () => sessions.version);
  useSyncExternalStore(skillEdits.subscribe, () => skillEdits.version);
  const rows = sessions.active();
  const edits = skillEdits.active();
  const all = threads.kind === "loaded" ? threads.threads : [];
  const live = new Map(rows.map((session) => [session.threadId!, session]));
  const recent = recentThreads(all, new Set(live.keys()));

  return (
    <section className="app-chats" aria-label={t("shell.chats.title")}>
      <div className="app-running-head">{t("shell.chats.title")}</div>

      {edits.length > 0 ? (
        <ul className="app-nav app-chats-pinned">
          {edits.map((edit) => {
            const waiting = !edit.state.streaming && edit.state.interrupt !== null;
            const title = t("shell.running.editing", { name: edit.label || t("shell.running.untitledSkill") });
            const status = waiting ? t("shell.running.needsApproval") : t("shell.running.working");
            return (
              <li key={`skill:${edit.skillId}`}>
                <button
                  type="button"
                  className={`app-nav-item app-running-item${waiting ? " is-waiting" : ""}`}
                  title={[title, t("shell.running.skillEditor"), status].join(" · ")}
                  onClick={() => onOpenSkill(edit.skillId)}
                >
                  <LiveIcon waiting={waiting} />
                  <span className="app-running-text">
                    <span className="app-running-title">{title}</span>
                    <span className="app-running-meta">
                      {waiting ? <span className="app-running-badge">{status}</span> : null}
                      {t("shell.running.skillEditor")}
                    </span>
                  </span>
                  {waiting ? null : <span className="visually-hidden">{status}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      <div className="app-chats-recent">
        {threads.kind === "idle" ? (
          <p className="app-chats-note">{t("projects.noneSelected")}</p>
        ) : threads.kind === "loading" ? (
          <p className="app-chats-note">
            <Spinner size={14} />
          </p>
        ) : threads.kind === "access-lost" ? (
          <p className="app-chats-note">{t("chat.projectAccessLost")}</p>
        ) : threads.kind === "error" ? (
          <p className="app-chats-note">
            {threads.message}{" "}
            <button type="button" className="app-chats-link" onClick={onRetry}>
              {t("projects.retry")}
            </button>
          </p>
        ) : all.length === 0 ? (
          <p className="app-chats-note">{t("chat.noThreads")}</p>
        ) : (
          <ul className="app-nav">
            {recent.map((thread) => {
              const title = thread.title || t("chat.newConversation");
              // This project's running conversation says so on its own row.
              const session = live.get(thread.thread_id);
              const waiting = session ? isWaiting(session) : false;
              const status = session ? (waiting ? t("shell.running.needsApproval") : t("shell.running.working")) : null;
              return (
                <li key={thread.thread_id}>
                  <button
                    type="button"
                    className={`app-nav-item app-chat-item${session ? " is-live" : ""}${waiting ? " is-waiting" : ""}`}
                    aria-current={thread.thread_id === currentThreadId ? "page" : undefined}
                    title={[title, status].filter(Boolean).join(" · ")}
                    onClick={() => onOpen(thread.thread_id, currentProjectId)}
                  >
                    {session ? <LiveIcon waiting={waiting} /> : null}
                    <span className="app-chat-title">{title}</span>
                    {waiting ? <span className="app-running-badge">{status}</span> : null}
                    {session && !waiting ? <span className="visually-hidden">{status}</span> : null}
                  </button>
                </li>
              );
            })}
            {all.length > recent.length ? (
              <li>
                <button
                  type="button"
                  className="app-nav-item app-chat-item app-chats-all"
                  aria-current={viewAllActive ? "page" : undefined}
                  onClick={onViewAll}
                >
                  <span className="app-chat-title">{t("shell.chats.viewAll", { count: String(all.length) })}</span>
                  <span className="app-chats-all-caret" aria-hidden="true">
                    ›
                  </span>
                </button>
              </li>
            ) : null}
          </ul>
        )}
      </div>
    </section>
  );
}
